import { useRolodexEntries, useRolodexSignedURLs } from 'features/rolodex'
import compact from 'lodash/compact'
import { RugFormDataState } from 'pages/reporting/reports/rug/rugReportData'
import { useCallback, useMemo } from 'react'
import { GeckoFileCategory, RolodexEntryType } from 'types'

export interface AssetPicture {
  imagePath: string
  signedUrl?: string
  imageCaption?: string
}

export const useAssetPictures = (formData: RugFormDataState | undefined) => {
  const reportIds = formData?.imagesSectionData.imageReportIds

  const {
    reports,
    getReport,
    forceRefresh: forceRefreshReports,
  } = useRolodexEntries(
    reportIds
      ? [
          {
            type: RolodexEntryType.REPORT,
            ids: reportIds,
          },
        ]
      : undefined
  )

  const imageReports = useMemo(() => {
    if (!reports) return []
    return reports.filter(
      (report) => report.data.file_category === GeckoFileCategory.IMAGE
    )
  }, [reports])

  const photoFilePaths = useMemo(() => {
    return imageReports.map((report) => ({
      entry_id: report.id,
      path: report.id,
    }))
  }, [imageReports])

  const {
    results: photoSignedUrls,
    forceRefresh: forceRefreshPhotoSignedUrls,
  } = useRolodexSignedURLs(photoFilePaths)

  const forceRefresh = useCallback(async () => {
    await forceRefreshReports()
    await forceRefreshPhotoSignedUrls()
  }, [forceRefreshReports, forceRefreshPhotoSignedUrls])

  const assetPictures = useMemo(() => {
    return compact(
      photoSignedUrls.map((result) => {
        const report = getReport(result.entry_id)

        if (!report) {
          return undefined
        }

        return {
          imagePath: result.path,
          signedUrl: result.signed_url,
          imageCaption: report.data.file_caption,
        }
      })
    )
  }, [photoSignedUrls, getReport])

  return {
    assetPictures,
    forceRefresh,
  }
}
